import { createSlice, createAsyncThunk, current, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import getConfig from "../../app/config";
import fetchWithRefresh from "../../services/fetchWithRefresh";
import { getRandomColor } from "../../components/random";
import { ScaleMetadata as ScaleMetadata, TrackDataCache, buildCumulativeGroupsFromTracks, getCacheKey, computeOptimalResolutionsForTrackTypes, hasAdequateCacheCoverage } from "./resolutionHelpers";
import { RootState } from "../../app/store";

export const getTemplates = createAsyncThunk<any, {templateType: number, clientId: string, projectId: string, boreholeId: string}>(
    "template/getTemplates",
    async (arg) => {
      const { clientId, projectId, templateType, boreholeId } = arg;                                                    
      return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/project/${projectId}/borehole/${boreholeId}/template/list/${templateType}`);
    }
)

export const getClientTemplates = createAsyncThunk<any, {templateType: number, clientId: string, projectId: string}>(
  "template/getClientTemplates",
  async (arg) => {
    const { clientId, projectId, templateType } = arg; 
    let url = `client/${clientId}/template/clientlist/${templateType}`;                                                   
    if (projectId) {
      url += `?projectId=${projectId}`;
    }

    return await fetchWithRefresh(getConfig().apiUrl + url);
  }
)

export const getTemplate = createAsyncThunk<string, string>(
  "template/getTemplate",
  async (id) => {
    return await fetchWithRefresh(getConfig().apiUrl + `template/${id}`);    
  }
)

// Single POST to fetch many track types with individual scales
export const postTrackDataForTrackTypesWithResolutions = createAsyncThunk<any, {
  clientId: string,
  projectId: string,
  boreholeId: string,
  pixelHeight: number,
  trackTypes: Array<{ trackTypeId: number; scale: number }>,
  byBoreholeDepth: boolean,
  startDepth: number,
  endDepth: number,
  depthUnit?: number | null
}>(
  "template/postTrackDataForTrackTypesWithResolutions",
  async (arg, thunkApi) => {
    const { clientId, projectId, boreholeId } = arg;
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/project/${projectId}/borehole/${boreholeId}/trackData`, thunkApi, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(arg),
    });
  }
)

// Multi-borehole POST with per-borehole-per-track scales
export const postMultiBoreholeTrackDataWithScales = createAsyncThunk<any, {
    clientId: string,
    boreholeEntries: Array<{
      boreholeId: string | number;
      projectId: string;
      trackTypeEntries: Array<{ trackTypeId: number; scale: number }>;
    }>,
    pixelHeight: number,
    byBoreholeDepth: boolean,
    startDepth: number,
    endDepth: number,
    depthUnit?: number | null
}>(
  "template/postMultiBoreholeTrackDataWithScales",
  async (arg, thunkApi) => {
    const { clientId } = arg;    
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/trackData/multiBorehole`, thunkApi, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(arg),
    });
  }
)

// Multi-borehole scale metadata fetching
export const getScaleMetadata = createAsyncThunk<any, {
  clientId: string,
  byBoreholeDepth: boolean,
  boreholes: Array<{
    boreholeId: string | number;
    projectId: string;
    trackTypeIds: number[];
    imageTypeIds: number[];
  }>
}>(
  "template/getScaleMetadata",
  async (arg, thunkApi) => {
    const { clientId, boreholes, byBoreholeDepth } = arg;
    return await fetchWithRefresh(
      getConfig().apiUrl + `client/${clientId}/trackData/scales`,
      thunkApi,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ boreholes, byBoreholeDepth }),
      }
    );
  }
)

export const fetchScalesAndTrackData = createAsyncThunk<any, {
  clientId: string,
  projectId: string,
  boreholes: Array<{
    boreholeId: string | number;
    projectId: string;
    trackTypeIds: number[];
    imageTypeIds: number[];
  }>;
  pixelHeight: number;
  byBoreholeDepth: boolean;
  startDepth: number;
  endDepth: number;
  depthUnit?: number | null;
  cumulativeGroups?: number[][];
  /** When true, skip the getScaleMetadata API call and reuse existing state.
   *  Use this when only panning / making minor depth range adjustments where
   *  the optimal scale resolution is unlikely to have changed. */
  skipScaleCheck?: boolean;
}>(
  "template/fetchScalesAndTrackData",
  async (arg, thunkApi) => {
    const { dispatch, getState } = thunkApi;
    const { clientId, projectId, boreholes, pixelHeight, byBoreholeDepth, startDepth, endDepth, depthUnit, cumulativeGroups, skipScaleCheck } = arg;

    if (!boreholes || boreholes.length === 0) return;
    const depthRange = endDepth - startDepth;
    if (depthRange <= 0) return;

    if (!skipScaleCheck) {
      await dispatch(getScaleMetadata({
        clientId,
        byBoreholeDepth,
        boreholes: boreholes.map((b) => ({
          boreholeId: b.boreholeId,
          projectId: b.projectId,
          trackTypeIds: b.trackTypeIds,
          imageTypeIds: b.imageTypeIds
        }))
      }));
    }

    const state = getState() as RootState;
    const scaleMetadataByBorehole = state.templates?.scaleMetadataByBorehole || {};
    const trackDataCache = state.templates?.trackDataCache || {};
    const groupTrackData = state.templates?.groupTrackData || [];
    const resolvedCumulativeGroups = Array.isArray(cumulativeGroups) && cumulativeGroups.length > 0
      ? cumulativeGroups
      : buildCumulativeGroupsFromTracks(state.templates?.template?.tracks);

    const noDataMap: { [boreholeId: number]: Set<number> } = {};
    groupTrackData.forEach((entry: any) => {
      const bId = Number(entry?.boreholeId);
      const ttId = Number(entry?.trackTypeId);
      if (isNaN(bId) || isNaN(ttId)) return;
      if (!noDataMap[bId]) noDataMap[bId] = new Set<number>();
      if (entry?.noData === true || entry?.data == null) {
        noDataMap[bId].add(ttId);
      }
    });

    const boreholeEntries: Array<{
      boreholeId: string | number;
      projectId: string;
      trackTypeEntries: Array<{ trackTypeId: number; scale: number }>;
    }> = [];

    boreholes.forEach((b) => {
      const bIdNum = typeof b.boreholeId === 'string' ? parseInt(b.boreholeId) : b.boreholeId;
      const metaArr = scaleMetadataByBorehole[String(b.boreholeId)] || [];
      const metaMap: { [trackTypeId: number]: any } = {};
      metaArr.forEach((m: any) => { metaMap[m.trackTypeId] = m; });

      const available = (b.trackTypeIds || []).filter((id) => metaMap[id]);
      if (available.length === 0) return;

      const entries = computeOptimalResolutionsForTrackTypes(
        available,
        metaMap as any,
        depthRange,
        2000,
        resolvedCumulativeGroups
      );

      const toFetch = entries.filter(({ trackTypeId, scale }) => {
        const isNoData = noDataMap[bIdNum]?.has(trackTypeId);
        if (isNoData) return false;
        return !hasAdequateCacheCoverage(
          trackDataCache,
          trackTypeId,
          bIdNum,
          scale,
          startDepth,
          endDepth
        );
      });

      if (toFetch.length > 0) {
        boreholeEntries.push({
          boreholeId: b.boreholeId,
          projectId: b.projectId || projectId,
          trackTypeEntries: toFetch
        });
      }
    });

    if (boreholeEntries.length === 0) return;

    return await dispatch(postMultiBoreholeTrackDataWithScales({
      clientId,
      boreholeEntries,
      pixelHeight,
      byBoreholeDepth,
      startDepth,
      endDepth,
      depthUnit: depthUnit ?? null
    }));
  }
)


export const getMultipleBoreholeTrackDataForTrackTypes = createAsyncThunk<any, { clientId: string, boreholeTrackTypes: any[], pixelHeight: number, byBoreholeDepth: boolean, boreholeImageTypes: number[], startDepth?: number | null, endDepth?: number | null, depthUnit?: number | null}>(
  "template/getMultipleBoreholeTrackDataForTrackTypes",
  async (arg, thunkApi) => {      
    const { clientId, boreholeTrackTypes, boreholeImageTypes } = arg;               
    if (boreholeTrackTypes.length === 0 && boreholeImageTypes.length === 0) {      
      return; 
    }    
        
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/trackData`, thunkApi, 
      {
        method: 'POST',
        headers: {       
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(arg),  
      });
  }
)

export const getMultipleBoreholeTrackDataForTemplate = createAsyncThunk<any, { clientId: string, templateId: any, boreholeIds: any[], pixelHeight: number, byBoreholeDepth: boolean, startDepth?: number | null, endDepth?: number | null, depthUnit?: number | null}>(
  "template/getMultipleBoreholeTrackDataForTemplate",
  async (arg, thunkApi) => {      
    const { clientId } = arg;        
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/trackData`, thunkApi, 
      {
        method: 'POST',
        headers: {       
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(arg),  
      });
  }
)

export const getMultipleBoreholeTrackDataForView = createAsyncThunk<any, { clientId: string, viewId: any, pixelHeight: number, byBoreholeDepth: boolean, startDepth?: number | null, endDepth?: number | null, depthUnit?: number | null}>(
  "template/getMultipleBoreholeTrackDataForView",
  async (arg, thunkApi) => {      
    const { clientId } = arg;   
     
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/trackData`, thunkApi, 
      {
        method: 'POST',
        headers: {       
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(arg),  
      });
  }
)

export const getMultipleBoreholeTextData = createAsyncThunk<any, { clientId: string, boreholeIds: any[], trackTypeId: any }>(
  "template/getMultipleBoreholeTextData",
  async (arg, thunkApi) => {      
    const { clientId, boreholeIds, trackTypeId } = arg;    

    const queryTrackTypeIds = boreholeIds.reduce((previous:string, boreholeId: any) => { return previous + `&boreholeIds=${boreholeId}` }, '');
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/trackData/textData?${queryTrackTypeIds}&trackTypeId=${trackTypeId}`, thunkApi);
  }
)

export const getMultipleBoreholeStratigraphyData = createAsyncThunk<any, { clientId: string, boreholeIds: any[], trackTypeId: any }>(
  "template/getMultipleBoreholeStratigraphyData",
  async (arg, thunkApi) => {      
    const { clientId, boreholeIds, trackTypeId } = arg;    

    const queryTrackTypeIds = boreholeIds.reduce((previous:string, boreholeId: any) => { return previous + `&boreholeIds=${boreholeId}` }, '');
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/trackData/stratigraphyData?${queryTrackTypeIds}&trackTypeId=${trackTypeId}`, thunkApi);
  }
)

export const getTrackDataForImageTrackTypes = createAsyncThunk<any, { clientId: string, projectId: string, boreholeId: string, imageTypes: number[] }>(
  "template/getTrackDataForImageTrackTypes",
  async (arg, thunkApi) => {
    const { clientId, projectId, boreholeId, imageTypes } = arg;
    const imageTypesQueryString = imageTypes.reduce((previousValue: any, currentValue: any) => { return previousValue + "imageTypes=" + currentValue + "&" }, "");
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/project/${projectId}/borehole/${boreholeId}/image/MetaData?${imageTypesQueryString}`, thunkApi);
  }
)

export const updateTemplate = createAsyncThunk<any, any>(
    "template/updateTemplate",
    async (template, thunkApi) => {            
      return await fetchWithRefresh(getConfig().apiUrl + "template", thunkApi, {
        method: 'PUT',
        headers: {       
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(template),
      });
    }
)

export const deleteTemplate = createAsyncThunk<any, any>(
  "template/deleteTemplate",
  async (args, thunkApi) => {
    const {templateId} = args;
    return await fetchWithRefresh(getConfig().apiUrl + "template", thunkApi, {
      method: 'DELETE',
      headers: {       
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ id: templateId }),
    });
  }
)

export const addTemplate = createAsyncThunk<any, any>(
  "template/addTemplate",
  async (template, thunkApi) => {    
    return await fetchWithRefresh(getConfig().apiUrl + "template", thunkApi, {
      method: 'POST',
      headers: {       
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(template),
    });
  }
)

export const changeTemplateLevel = createAsyncThunk<any, any>(
  "template/changeLevel",
  async (template, thunkApi) => {
    return await fetchWithRefresh(getConfig().apiUrl + `template/${template.id}/changeLevel`, thunkApi, {
      method: 'PUT',
      headers: {       
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(template),
    });
  }
)

export const saveLithologyData = createAsyncThunk<any, { clientId: string, projectId: string, boreholeId: number, data: any}>(
  "template/saveLithologyData",  
  async (arg, thunkApi) => {       
    const { clientId, projectId, boreholeId, data } = arg;
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/project/${projectId}/borehole/${boreholeId}/trackData`, thunkApi, {
      method: 'PUT',
      headers: {       
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(data),
    });
  }
)

export const saveTextData = createAsyncThunk<any, { clientId: string, projectId: string, boreholeId: number, trackTypeId:any, data: any}>(
  "template/saveTextData",
  async (arg, thunkApi) => {
    const { clientId, projectId, boreholeId, trackTypeId, data } = arg;
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/project/${projectId}/borehole/${boreholeId}/trackData/UpdateText`, thunkApi, {
      method: 'PUT',
      headers: {       
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({trackTypeId, data}),
    });
  }
)

export const saveStratigraphyData = createAsyncThunk<any, { clientId: string, projectId: string, boreholeId: number, trackTypeId:any, data: any}>(
  "template/saveStratigraphyData",
  async (arg, thunkApi) => {
    const { clientId, projectId, boreholeId, trackTypeId, data } = arg;
    return await fetchWithRefresh(getConfig().apiUrl + `client/${clientId}/project/${projectId}/borehole/${boreholeId}/trackData/UpdateStratigraphy`, thunkApi, {
      method: 'PUT',
      headers: {       
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({trackTypeId, data}),
    });
  }
)

const emptyTemplate = {
  cannotOverwrite: false,
  depthMajorIntervals: 6,
  depthMinorIntervals: 1,
  depthUnit: 1,
  errors: null,
  name: "",
  showDepthGridLines: true,
  showDepthMinorIntervals: false,
  showValueAxisAnnotation: true,
  tracks: [],
  statisticalTracks: []
}

export interface GroupTrackData {
  boreholeId: number;
  trackTypeId: number;
  data: any[];
  scale?: number;
  minValue?: number;
  maxValue?: number;
  noData?: boolean;
}

export interface TemplateLevel {
  value: number;
  name: string;
  canView: boolean;
  canAdd: boolean;
  canUpdate: boolean;
  canDelete: boolean;
}

export interface TemplateState {
    templatesLoading: boolean,
    templateLoading: boolean,
    templateLoaded: boolean,
    trackDataLoading: boolean,
    templatesLoaded: boolean,
    allTemplates: any[],
    builtInTemplates: any[],
    userTemplates: any[],
    boreholeTemplates: any[],
    projectTemplates: any[],    
    clientTemplates: any[],    
    templateLevels: TemplateLevel[],    
    selectedTemplateId: string | null,
    urlTemplateId: string | null,
    selectedBuiltInTemplateId: string | null,
    selectedUserTemplateId: string | null,
    selectedBoreholeTemplateId: string | null,
    selectedProjectTemplateId: string | null,
    selectedClientTemplateId: string | null,
    template: any,
    trackData: any[],
    groupTrackData: GroupTrackData[],
    trackImages: any[],
    groupTrackImages: any[],
    error: any,
    templateSaveError: any,
    notSaved: boolean,
    canSaveTemplate: boolean,    
    canDeleteTemplate: boolean,    
    canUpdateTemplate: boolean,    
    nextTemplateId: string | null,
    nextTemplateLevel: number | null,
    minDepth: number | null,
    maxDepth: number | null,
    deletedCurves: any[] | null,
    deepZoomMetaData: any,
    viewer: string | null,
    dataNotSaved: string[],
    scaleMetadata: ScaleMetadata[],
    // Per-borehole scale metadata for correlation/multi-borehole views
    scaleMetadataByBorehole: { [boreholeId: string]: ScaleMetadata[] },
    trackDataCache: TrackDataCache,
    loadingHighResolution: boolean,
    scalesLoaded: boolean,
    scalesLoading: boolean
  }
  
  const initialState: TemplateState = {
    templatesLoading: false,
    templateLoading: false,
    templateLoaded: false,
    trackDataLoading: false,
    templatesLoaded: false,
    allTemplates: [],
    builtInTemplates: [],
    userTemplates: [],
    boreholeTemplates: [],
    projectTemplates: [],
    clientTemplates: [],
    selectedTemplateId: null,
    templateLevels: [],
    urlTemplateId: null,
    selectedBuiltInTemplateId: null,
    selectedUserTemplateId: null,
    selectedBoreholeTemplateId: null,
    selectedProjectTemplateId: null,
    selectedClientTemplateId: null,
    template: emptyTemplate,
    trackData: [],
    groupTrackData: [],
    trackImages: [],
    groupTrackImages: [],
    error: null,
    templateSaveError: null,
    notSaved: false,
    canSaveTemplate: false,
    canDeleteTemplate: false,
    canUpdateTemplate: false,
    nextTemplateId: null,
    nextTemplateLevel: null,
    minDepth: null,
    maxDepth: null,
    deletedCurves: null,
    deepZoomMetaData: {},
    viewer: null,
    dataNotSaved: [],
    scaleMetadata: [],
    scaleMetadataByBorehole: {},
    trackDataCache: {},
    loadingHighResolution: false,
    scalesLoaded: false,
    scalesLoading: false
  }

  const sortTemplates = (state: TemplateState) => {
    const defaultTemplates = state.builtInTemplates.filter((t:any) => t.cannotOverwrite);
    let userTemplates = state.builtInTemplates.filter((t:any) => !t.cannotOverwrite);
    userTemplates.sort((a: any, b: any) => {
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });    
    state.allTemplates.sort((a: any, b: any) => {      
      return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
    });
    state.builtInTemplates = [...defaultTemplates, ...userTemplates];
  }

const getTrackCurve = (state: any, trackId: string, curveId: string) => {
   const templateTrack = state.template?.tracks.find((t: any) => t.id == trackId);
   return templateTrack?.curves.find((c: any) => c.id == curveId);
};

const getTrack = (state: any, trackId: string) => {
  return state.template?.tracks.find((t: any) => t.id == trackId);  
};

const templateSlice = createSlice({
    name: 'template',
    initialState,
    reducers: {      
      updateCurve: (state, action: PayloadAction<any>) => {        
        const { templateTrackId, curve } = action.payload;        
        let trackCurve = getTrackCurve(state, templateTrackId, curve.id);

        if (trackCurve) {
          trackCurve.lineColor = curve.lineColor;
          trackCurve.fillColor = curve.fillColor;
          trackCurve.pointColor = curve.pointColor;
          trackCurve.lineStyle = curve.lineStyle;
          trackCurve.curveType = curve.curveType;
          trackCurve.fillStyle = curve.fillStyle;
          trackCurve.pointStyle = curve.pointStyle;
          trackCurve.lineSize = curve.lineSize;
          trackCurve.fillSize = curve.fillSize;
          trackCurve.pointSize = curve.pointSize;
          trackCurve.showLine = curve.showLine;
          trackCurve.showFill = curve.showFill;
          trackCurve.showPoints = curve.showPoints;

          trackCurve.xScaleMinimum = curve.xScaleMinimum;
          trackCurve.xScaleMaximum = curve.xScaleMaximum;
          trackCurve.xScaleMinimumExtent = curve.xScaleMinimumExtent;
          trackCurve.xScaleMaximumExtent = curve.xScaleMaximumExtent;
          trackCurve.manualScaleXMinimum = curve.manualScaleXMinimum;
          trackCurve.manualScaleXMaximum = curve.manualScaleXMaximum;
          trackCurve.majorIntervals = curve.majorIntervals;
          trackCurve.minorIntervals = curve.minorIntervals;
          trackCurve.manualMajorIntervals = curve.manualMajorIntervals;
          trackCurve.manualMinorIntervals = curve.manualMinorIntervals;
          trackCurve.logLessThaZero = curve.logLessThaZero;

          //state.notSaved = true;
        }        
      },
      addCurve: (state, action: PayloadAction<any>) => {        
        const { trackId } = action.payload;              
          
        const templateTrack = state.template?.tracks.find((t: any) => t.id == trackId);
        templateTrack?.curves.push({ new: true });
      },
      updateCurves: (state, action: PayloadAction<any>) => {        
        const { templateTrackId, curves } = action.payload;    
        const templateTrack = state.template?.tracks.find((t: any) => t.id == templateTrackId);
        if (!templateTrack || !Array.isArray(curves)) {
          return;
        }
        
        // find ones that no longer exist
        const curveIds = curves.map((curve: any) => curve.id);
        const deletedCurves = templateTrack?.curves.filter((curve: any) => !curveIds.includes(curve.id));
        deletedCurves?.forEach((curve: any) => {
          const curveIndex = templateTrack?.curves.findIndex((index: any) => index.id === curve.id);
          if (curveIndex !== undefined && curveIndex !== -1) {
            templateTrack?.curves.splice(curveIndex, 1);
          }
        });

        state.deletedCurves = deletedCurves;

        curves.forEach((curve: any) => { 
          let trackCurve = getTrackCurve(state, templateTrackId, curve.id);
          if (trackCurve) {          
            trackCurve.displayOrder = curve.displayOrder;
            trackCurve.trackTypeId = curve.trackTypeId;
            trackCurve.lineColor = curve.lineColor;
            trackCurve.fillColor = curve.fillColor;
            trackCurve.pointColor = curve.pointColor;
            trackCurve.lineStyle = curve.lineStyle;
            trackCurve.curveType = curve.curveType;
            trackCurve.fillStyle = curve.fillStyle;
            trackCurve.pointStyle = curve.pointStyle;
            trackCurve.lineSize = curve.lineSize;
            trackCurve.fillSize = curve.fillSize;
            trackCurve.pointSize = curve.pointSize;
            trackCurve.showLine = curve.showLine;
            trackCurve.showFill = curve.showFill;
            trackCurve.showPoints = curve.showPoints;

            trackCurve.xScaleMinimum = curve.xScaleMinimum;
            trackCurve.xScaleMaximum = curve.xScaleMaximum;
            trackCurve.manualScaleXMinimum = curve.manualScaleXMinimum;
            trackCurve.manualScaleXMaximum = curve.manualScaleXMaximum;
            trackCurve.majorIntervals = curve.majorIntervals;
            trackCurve.minorIntervals = curve.minorIntervals;
            trackCurve.manualMajorIntervals = curve.manualMajorIntervals;
            trackCurve.manualMinorIntervals = curve.manualMinorIntervals;
            trackCurve.logLessThaZero = curve.logLessThaZero;
            trackCurve.colorBasedOnLithology = curve.colorBasedOnLithology;
            trackCurve.lithologyTrackTypeId = curve.lithologyTrackTypeId;
          } else {            
            curve.new = false;
            templateTrack.curves.push(curve);
          }
        });

        templateTrack.curves.sort((a: any, b:any) => a.displayOrder - b.displayOrder);
        state.notSaved = true;
      },
      setShowGridlines: (state, action: PayloadAction<any>) => {      

        const { templateTrackId, show } = action.payload;   
        let track = getTrack(state, templateTrackId);
        if (track) {
          track.showGridlines = show;
          state.notSaved = true;
        }        
      },      
      setScaleType: (state, action: PayloadAction<any>) => {        
        const { templateTrackId, scaleType } = action.payload;   
        let track = getTrack(state, templateTrackId);
        if (track) {
          track.scaleType = scaleType;
          state.notSaved = true;
        }        
      },
      setTrackImageFilter: (state, action: PayloadAction<any>) => {        
        const { templateTrackId, filter } = action.payload;   
        
        let track = getTrack(state, templateTrackId);
        if (track) {     
                  
          const changed = track.imageFilter != filter;
          track.imageFilter = filter;
          state.notSaved = changed;
        }        
      },
      setTrackWidth: (state, action: PayloadAction<any>) => {        
        const { templateTrackId, width } = action.payload;           
        const track = state.template?.tracks.find((t: any) => t.id == templateTrackId);        
        if (track) {
          track.trackWidth = Math.round(width);
          state.notSaved = true;
        }        
      },
      setSelectedBuiltInTemplateId: (state, action: PayloadAction<any>) => {             
        const selectedTemplateId = action.payload;        
        state.selectedTemplateId = selectedTemplateId;
        state.selectedBuiltInTemplateId = null;          
        state.selectedUserTemplateId = null;
        state.selectedBoreholeTemplateId = null;
        state.selectedProjectTemplateId = null;
        state.selectedClientTemplateId = null;
        const selectedTemplate = state.allTemplates?.find((t :any) => t.id === selectedTemplateId);        
        state.canSaveTemplate = false;
        state.canDeleteTemplate = false;
      },
      setSelectedUserTemplateId: (state, action: PayloadAction<any>) => {      
        const selectedTemplateId = action.payload;    
        state.selectedTemplateId = selectedTemplateId;
        state.selectedBuiltInTemplateId = null;      
        state.selectedUserTemplateId = null;  
        state.selectedBoreholeTemplateId = null;
        state.selectedProjectTemplateId = null;
        state.selectedClientTemplateId = null;
        const selectedTemplate = state.allTemplates?.find((t :any) => t.id === selectedTemplateId);
        state.canSaveTemplate = !selectedTemplate?.cannotOverwrite;        
        state.canDeleteTemplate = true;
      },
      setSelectedBoreholeTemplateId: (state, action: PayloadAction<any>) => {    
        const selectedTemplateId = action.payload;    
        state.selectedTemplateId = selectedTemplateId;  
        state.selectedBuiltInTemplateId = null;      
        state.selectedUserTemplateId = null;    
        state.selectedBoreholeTemplateId = null;  
        state.selectedProjectTemplateId = null;
        state.selectedClientTemplateId = null;
        const selectedTemplate = state.allTemplates?.find((t :any) => t.id === selectedTemplateId);
        state.canSaveTemplate = !selectedTemplate?.cannotOverwrite;
        state.canDeleteTemplate = false;
      },
      setSelectedProjectTemplateId: (state, action: PayloadAction<any>) => {    
        const selectedTemplateId = action.payload;    
        state.selectedTemplateId = selectedTemplateId;
        state.selectedBuiltInTemplateId = null;  
        state.selectedUserTemplateId = null;
        state.selectedBoreholeTemplateId = null;        
        state.selectedProjectTemplateId = null;  
        state.selectedClientTemplateId = null;
        const selectedTemplate = state.allTemplates?.find((t :any) => t.id === selectedTemplateId);
        state.canSaveTemplate = !selectedTemplate?.cannotOverwrite;
        state.canDeleteTemplate = false;
      },
      setSelectedClientTemplateId: (state, action: PayloadAction<any>) => {  
        const selectedTemplateId = action.payload; 
        state.selectedTemplateId = selectedTemplateId;
        state.selectedBuiltInTemplateId = null;
        state.selectedUserTemplateId = null;
        state.selectedBoreholeTemplateId = null;
        state.selectedProjectTemplateId = null;
        state.selectedClientTemplateId = null;  
        const selectedTemplate = state.allTemplates?.find((t :any) => t.id === selectedTemplateId);        
        state.canSaveTemplate = !selectedTemplate?.cannotOverwrite;
        state.canDeleteTemplate = false;
      },
      setSelectedTemplateId: (state, action: PayloadAction<any>) => {  
        const selectedTemplateId = action.payload; 
        state.selectedTemplateId = selectedTemplateId;         
        const selectedTemplate = state.allTemplates?.find((t :any) => t.id === selectedTemplateId);        
        const templateLevel = state.templateLevels?.find((l: any) => l.value === selectedTemplate?.templateLevel);
        state.canSaveTemplate = !selectedTemplate?.cannotOverwrite;
        state.canDeleteTemplate = templateLevel?.canDelete || false;
        state.canUpdateTemplate = templateLevel?.canUpdate || false;
      },
      setNextTemplateId:(state, action: PayloadAction<any>) => {
        state.nextTemplateId = action.payload?.nextTemplateId;
        state.nextTemplateLevel = action.payload?.nextTemplateLevel;
      },
      clearNotSaved:(state) => {
        state.notSaved = false;
        state.dataNotSaved = [];
      },    
      addGraphTracks: (state, action: PayloadAction<any>) => {          
        action.payload.boreholeIds.forEach((boreholeId: any) => {
          action.payload.trackTypes.forEach((trackType: any) => {
            const color = getRandomColor();
            let myuuid = uuidv4();
            let curveId = uuidv4();
            state.template?.tracks.push({ 
              id: myuuid,                            
              trackType: 0,
              displayOrder: (state.template?.tracks.length ?? 0) + 1, 
              new: true, 
              scaleType: 0,
              trackWidth: 150,
              showGridlines: true,
              showValueAxisAnnotation: state.template?.showValueAxisAnnotation,
              curves:[{
                id: curveId,
                boreholeId,
                displayName: trackType.displayName,
                displayOrder: 1,
                fillColor: color + "80",
                fillSize: 5,
                fillStyle: "",
                lineColor: color,
                lineSize: 1,
                lineStyle: "line",
                majorIntervals: 6,
                manualMajorIntervals: false,
                manualMinorIntervals: false,
                manualScaleXMaximum: false,
                manualScaleXMinimum: false,
                minorIntervals: 1,
                pointColor: color,
                pointSize: 5,
                pointStyle: "circle",            
                showFill: true,            
                showLine: true,
                showPoints: false,
                curveType: trackType.allowedCurveTypes[0],
                trackTypeId: trackType.trackTypeId,
                units: trackType.units,
                xScaleMaximum: null,
                xScaleMinimum: null
              }]
            });
          });
        });

        state.notSaved = true;
      },
      addHorizontalBarGraphTracks: (state, action: PayloadAction<any>) => {  
        action.payload.boreholeIds.forEach((boreholeId: any) => {          
          action.payload.trackTypes.forEach((trackType: any) => {            
            const color = getRandomColor();
            let myuuid = uuidv4();
            let curveId = uuidv4();
            state.template?.tracks.push({ 
              id: myuuid,                            
              trackType: 8,
              displayOrder: state.template?.tracks.length + 2, 
              new: true, 
              scaleType: 0,
              trackWidth: 150,
              showGridlines: true,
              showValueAxisAnnotation: state.template?.showValueAxisAnnotation,
              curves:[{
                id: curveId,
                boreholeId,
                displayName: trackType.displayName,
                displayOrder: 1,
                fillColor: color + "80",
                fillSize: 5,
                fillStyle: "",
                lineColor: color,
                lineSize: 1,
                lineStyle: "line",
                majorIntervals: 6,
                manualMajorIntervals: false,
                manualMinorIntervals: false,
                manualScaleXMaximum: false,
                manualScaleXMinimum: false,
                minorIntervals: 1,
                pointColor: color,
                pointSize: 5,
                pointStyle: "circle",            
                showFill: true,            
                showLine: true,
                showPoints: false,
                curveType: trackType.allowedCurveTypes[0],
                trackTypeId: trackType.trackTypeId,
                units: trackType.units,
                xScaleMaximum: null,
                xScaleMinimum: null
              }]
            });
          });
        });

        state.notSaved = true;
      },
      addImageTracks: (state, action: PayloadAction<any>) => {          
        action.payload.boreholeIds.forEach((boreholeId: any) => {
          action.payload.imageTrackTypes.forEach((imageTrackType: any) => {
            let myuuid = uuidv4();            
            state.template?.tracks.push({ 
              id: myuuid,
              boreholeId,
              trackType: 1,
              displayOrder: state.template?.tracks.length + 2, 
              new: true,
              displayName: imageTrackType.displayName + " Image",
              imageType: imageTrackType.imageType,
              legend: imageTrackType?.legendName, 
              legendRollover: imageTrackType?.legendRollover,
              trackWidth: 150
            });
          });
        });

        state.notSaved = true;
      },
      addCumulativeTrack: (state, action: PayloadAction<any>) => {  

        const curves: any[] = [];
        let displayOrder = 1;
        action.payload.boreholeIds.forEach((boreholeId: any) => {
          action.payload.trackTypes.forEach((trackType: any) => {
            const color = getRandomColor();
            let curveId = uuidv4();
            curves.push({ 
              id: curveId,
              boreholeId,
              displayName: trackType.displayName,
              displayOrder: displayOrder++,
              fillColor: color + "80",
              fillSize: 5,
              fillStyle: "",
              lineColor: color,
              lineSize: 1,
              lineStyle: "line",
              majorIntervals: 6,
              manualMajorIntervals: false,
              manualMinorIntervals: false,
              manualScaleXMaximum: false,
              manualScaleXMinimum: false,
              minorIntervals: 1,
              pointColor: color,
              pointSize: 5,
              pointStyle: "circle",            
              showFill: true,            
              showLine: true,
              showPoints: false,
              trackTypeId: trackType.trackTypeId,
              units: "",
              xScaleMaximum: null,
              xScaleMinimum: null });
          });
        });

        let myuuid = uuidv4();
        state.template?.tracks.push({ 
          id: myuuid,
          trackType: 3,
          displayOrder: state.template?.tracks.length + 2, 
          new: true, 
          scaleType: 0,
          trackWidth: 150,
          showGridlines: true,
          showValueAxisAnnotation: state.template?.showValueAxisAnnotation,
          curves:curves});
        
        state.notSaved = true;
      },
      addTadpoleTrack: (state, action: PayloadAction<any>) => {  
        const curves: any[] = [];
        let displayOrder = 1;
        const color = getRandomColor();
        action.payload.trackTypes.forEach((trackType: any) => {          
          let curveId = uuidv4();
          curves.push({ 
            id: curveId,
            displayName: trackType.displayName,
            displayOrder: displayOrder++,
            fillColor: color + "80",
            fillSize: 5,
            fillStyle: "",
            lineColor: color,
            lineSize: 1,
            lineStyle: "line",
            majorIntervals: 6,
            manualMajorIntervals: false,
            manualMinorIntervals: false,
            manualScaleXMaximum: false,
            manualScaleXMinimum: false,
            minorIntervals: 1,
            pointColor: color,
            pointSize: 5,
            pointStyle: "circle",            
            showFill: false,
            showLine: true,
            showPoints: true,
            trackTypeId: trackType.trackTypeId,
            units: "",
            xScaleMaximum: null,
            xScaleMinimum: null});
        });

        let myuuid = uuidv4();
        state.template?.tracks.push({ 
          id: myuuid,
          trackType: 4,
          displayOrder: state.template?.tracks.length + 2, 
          new: true, 
          scaleType: 0,
          trackWidth: 150,
          showGridlines: true,
          showValueAxisAnnotation: state.template?.showValueAxisAnnotation,
          curves });
        
        state.notSaved = true;
      },
      addLithologyTrack: (state, action: PayloadAction<any>) => {          
        action.payload.trackTypes.forEach((trackType: any) => {
          let myuuid = uuidv4();
          state.template?.tracks.push({ 
            id: myuuid,
            trackType: 5,
            displayOrder: state.template?.tracks.length + 2, 
            new: true, 
            scaleType: 0,
            trackWidth: 100,
            showGridlines: true,
            showValueAxisAnnotation: state.template?.showValueAxisAnnotation,
            lithologyType: trackType.trackTypeId,
            displayName: trackType.displayName,
            curves:[{trackTypeId: trackType.trackTypeId}]});
        });
        
        state.notSaved = true;
      },
      addStratigraphyTrack: (state, action: PayloadAction<any>) => {                  
        action.payload.trackTypes.forEach((trackType: any) => {
          let myuuid = uuidv4();
          state.template?.tracks.push({ 
            id: myuuid,
            trackType: 9,
            displayOrder: state.template?.tracks.length + 2, 
            new: true, 
            scaleType: 0,
            trackWidth: 100,
            showGridlines: true,
            showValueAxisAnnotation: state.template?.showValueAxisAnnotation,
            stratigraphyTrackTypeId: trackType.trackTypeId,
            displayName: trackType.displayName,
            curves:[{trackTypeId: trackType.trackTypeId}]});
        });
        
        state.notSaved = true;
      },
      addTextTrack: (state, action: PayloadAction<any>) => {              
        action.payload.trackTypes.forEach((trackType: any) => {            
          let myuuid = uuidv4();          
          state.template?.tracks.push({ 
            id: myuuid,
            trackType: 7,
            textTrackTypeId: trackType.trackTypeId,
            displayOrder: state.template?.tracks.length + 2, 
            new: true, 
            scaleType: 0,
            trackWidth: 300,            
            displayName: trackType.displayName});
          });

        state.notSaved = true;
      },
      addStatisticsTracks: (state, action: PayloadAction<any>) => {  
        action.payload.trackTypes.forEach((trackType: any) => {          
          if (!state.template?.statisticsTracks) {
            state.template.statisticsTracks = [];
          }

          state.template?.statisticsTracks.push(trackType);
        });

        state.notSaved = true;
      },
      deleteStatisticsTrack: (state, action: PayloadAction<any>) => {          
        const index = state.template.statisticsTracks.findIndex((t: any) => t.trackTypeId === action.payload.trackTypeId);
        if (index > -1) {
          state.template.statisticsTracks.splice(index,1);
        }        

        state.notSaved = true;
      },
      removeTrack: (state, action: PayloadAction<any>) => {  
        const { trackTypeId } = action.payload;        
        const index = state.template?.tracks.findIndex((track:any) => track.id === trackTypeId);
        if (index > -1) {
          state.template?.tracks.splice(index, 1);
          state.notSaved = true;
        }
      }, 
      updateTrackOrder: (state, action: PayloadAction<any>) => {  
        const { sourceIndex, destinationIndex } = action.payload;
        if (sourceIndex !== null && destinationIndex !== null) {                 
          const track = state.template?.tracks.splice(sourceIndex, 1)[0];          
          state.template?.tracks.splice(destinationIndex, 0, track);
          state.notSaved = true;
        }
      },      
      scrollTrackIntoView: (state, action: PayloadAction<any>) => {  
        const { trackId } = action.payload;   
        state.template?.tracks.forEach((t : any) => t.scrollIntoView = false);
        const track = state.template?.tracks.find((t: any) => t.id == trackId);
        if (track) {
          track.scrollIntoView = true;
        }
      },
      scrollAdjustmentIntoView: (state, action: PayloadAction<any>) => {  
        const { trackId } = action.payload;   
        state.template?.tracks.forEach((t : any) => t.scrollAdjustmentIntoView = false);
        const track = state.template?.tracks.find((t: any) => t.id == trackId);
        if (track) {
          track.scrollAdjustmentIntoView = true;
        }
      },
      setTrackCollapsed:  (state, action: PayloadAction<any>) => {  
        const { trackId, collapsed } = action.payload;                        
        const track = state.template?.tracks.find((t: any) => t.id == trackId);
        if (track) {
          track.collapsed = collapsed;
        }
      },
      setDepthUnit: (state, action: PayloadAction<any>) => {
        state.template.depthUnit = action.payload;
        state.notSaved = true;
      },
      setTemplateName: (state, action: PayloadAction<string>) => {
        if (state.template) {
          state.template.name = action.payload;
          state.notSaved = true;
        }
      },
      setDepthMajorIntervals: (state, action: PayloadAction<any>) => {
        state.template.depthMajorIntervals = action.payload;
        state.notSaved = true;
      },
      setShowDepthGridlines: (state, action: PayloadAction<any>) => {
        state.template.showDepthGridLines = action.payload;
        state.notSaved = true;
      },
      setDepthMinorIntervals: (state, action: PayloadAction<any>) => {
        state.template.depthMinorIntervals = action.payload;
        state.notSaved = true;
      },
      setShowDepthMinorIntervals: (state, action: PayloadAction<any>) => {
        state.template.showDepthMinorIntervals = action.payload;
        state.notSaved = true;
      },
      setShowValueAxisAnnotation: (state, action: PayloadAction<any>) => {
        const showValueAxisAnnotation = action.payload;
        state.template.showValueAxisAnnotation = showValueAxisAnnotation;
        state.template?.tracks.forEach((track: any) => {track.showValueAxisAnnotation = showValueAxisAnnotation});
        state.notSaved = true;
      },
      clearTemplatesLoaded: (state) => {
        state.template = {...emptyTemplate};
        state.allTemplates = [];
        state.templatesLoaded = false;
        state.selectedTemplateId = null;
      },
      clearTemplateLoading: (state) => {
        state.templateLoading = true;
      },
      clearTemplate: (state) => {        
        state.template = {...emptyTemplate};
        state.selectedTemplateId = null;
        state.deletedCurves = null;
      },
      setViewer: (state, action: PayloadAction<any>) => {
        state.viewer = action.payload;
      },      
      changeDepth: (state, action: PayloadAction<any>) => {
        const { trackTypeId, id, newDepth } = action.payload;
        const trackData = state.trackData.find((t: any) => t.trackTypeId === trackTypeId)?.data;
        if (!trackData) {
          return;
        }
        
        const data = trackData.find((d: any) => d.id === id);
        if (+newDepth !== data.depth) {
          data.depth = +newDepth;
          trackData.sort((a: any, b: any) => a.depth - b.depth);
          if (state.dataNotSaved.findIndex((t: any) => t == trackTypeId) === -1) {
            state.dataNotSaved.push(trackTypeId);
          }
        }
      },
      addDataValue: (state, action: PayloadAction<any>) => {
        const { id, trackTypeId, depth, value, color, lithologyLexiconId, showView, boreholeId } = action.payload;
        
        if (showView) {
          let track = state.groupTrackData.find((t: any) => t.trackTypeId === trackTypeId && t.boreholeId === boreholeId);
          if (!track) {
            track = { trackTypeId, boreholeId, data: [] };
            state.groupTrackData.push(track);
          }
  
          if (!track.data) {
            track.data = [];
          }
  
          const trackData = track.data;        
          if (trackData.some((item: any) => Number(item?.depth) === Number(depth))) {
            return;
          }
          
          // Use negative IDs for new items to distinguish from database IDs
          const newId = id ?? Math.min(0, ...trackData.map((item: any) => typeof item.id === 'number' ? item.id : 0)) - 1;
          const data = { id: newId, depth, value, color, lithologyLexiconId };
          trackData.push(data);
          trackData.sort((a: any, b: any) => a.depth - b.depth);
          if (state.dataNotSaved.findIndex((t: any) => t == trackTypeId) === -1) {
            state.dataNotSaved.push(trackTypeId);
          }
        } else {
          let track = state.trackData.find((t: any) => t.trackTypeId === trackTypeId);
          if (!track) {
            track = { trackTypeId, data: [] };
            state.trackData.push(track);
          }
  
          if (!track.data) {
            track.data = [];
          }
  
          const trackData = track.data;        
          if (trackData.some((item: any) => Number(item?.depth) === Number(depth))) {
            return;
          }
          
          // Use negative IDs for new items to distinguish from database IDs
          const newId = id ?? Math.min(0, ...trackData.map((item: any) => typeof item.id === 'number' ? item.id : 0)) - 1;
          const data = { id: newId, depth, value, color, lithologyLexiconId };
          trackData.push(data);
          trackData.sort((a: any, b: any) => a.depth - b.depth);
          if (state.dataNotSaved.findIndex((t: any) => t == trackTypeId) === -1) {
            state.dataNotSaved.push(trackTypeId);
          }
        }
      },
      changeDataValue: (state, action: PayloadAction<any>) => {
        const { id, depth, trackTypeId, value, color, lithologyLexiconId, showView, boreholeId } = action.payload;        
        const trackData = showView
          ? state.groupTrackData.find((t: any) => Number(t.trackTypeId) === Number(trackTypeId) && Number(t.boreholeId) === Number(boreholeId))?.data
          : state.trackData.find((t: any) => Number(t.trackTypeId) === Number(trackTypeId))?.data;

        const hasRowId = typeof id === 'number' && !Number.isNaN(id);
        const hasDepth = typeof depth === 'number' && !Number.isNaN(depth);
        const data = trackData?.find((d: any) => {
          if (hasRowId && typeof d?.id === 'number') {
            return d.id === id;
          }
          if (!hasRowId && hasDepth) {
            return Number(d?.depth) === Number(depth);
          }
          return false;
        });

        if (data) {
          data.lithologyLexiconId = lithologyLexiconId;
          data.value = value;
          data.color = color;
        }
        if (state.dataNotSaved.findIndex((t: any) => t == trackTypeId) === -1) {
          state.dataNotSaved.push(trackTypeId);
        }
      },
      // this is also used for deleting text data
      deleteDataValue: (state, action: PayloadAction<any>) => {
        const { trackTypeId, id, showView, boreholeId } = action.payload;
        const trackData = showView ? state.groupTrackData.find((t: any) => t.trackTypeId === trackTypeId && t.boreholeId === boreholeId)?.data : state.trackData.find((t: any) => t.trackTypeId === trackTypeId)?.data;
        if (trackData) {
          const dataIndex = trackData.findIndex((d: any) => d.id === id);
          if (dataIndex >= 0) {
            trackData.splice(dataIndex, 1);
          }
        }
        if (state.dataNotSaved.findIndex((t: any) => t == trackTypeId) === -1) {
          state.dataNotSaved.push(trackTypeId);
        }
      }, 
      addTextValue: (state, action: PayloadAction<any>) => {        
        const { id, textTrackTypeId, fromDepth, toDepth, value, showView, boreholeId } = action.payload;
        // sortedTrackData reads text/stratigraphy data from groupTrackData regardless of mode,
        // so always search and write there.
        let track = state.groupTrackData.find((t: any) => t.trackTypeId === textTrackTypeId && t.boreholeId === boreholeId);
        if (!track) {
          track = { trackTypeId: textTrackTypeId, boreholeId, data: [] };
          state.groupTrackData.push(track);
        }

        if (!track.data) {
          track.data = [];
        }

        const trackData = track.data;        
        
        // Use negative IDs for new items (e.g., -1, -2, -3) to distinguish from database IDs
        // Find the lowest existing negative ID and decrement it
        const newId = id ?? Math.min(0, ...trackData.map((item: any) => typeof item.id === 'number' ? item.id : 0)) - 1;
        const data = { id: newId, fromDepth, toDepth, value };
        trackData.push(data);
        trackData.sort((a: any, b: any) => a.fromDepth - b.fromDepth);
        if (state.dataNotSaved.findIndex((t: any) => t == textTrackTypeId) === -1) {
          state.dataNotSaved.push(textTrackTypeId);
        }
      },
      changeTextData: (state, action: PayloadAction<any>) => {        
        const { trackTypeId, data, showView, boreholeId } = action.payload;
        const normalizedData = (Array.isArray(data) ? data : []).map((row: any) => {
          const bg = row?.backgroundColor ?? row?.color;
          return bg ? { ...row, backgroundColor: bg, color: bg } : row;
        });

        // Text tracks are rendered from groupTrackData in both modes.
        let track = state.groupTrackData.find((t: any) => t.trackTypeId === trackTypeId && t.boreholeId === boreholeId);
        if (!track) {
          track = { trackTypeId, boreholeId, data: [] };
          state.groupTrackData.push(track);
        }
        track.data = normalizedData;

        // Keep trackData mirrored for code paths that still read from it in template mode.
        let baseTrack = state.trackData.find((t: any) => t.trackTypeId === trackTypeId);
        if (!baseTrack) {
          baseTrack = { trackTypeId, data: [] };
          state.trackData.push(baseTrack);
        }
        baseTrack.data = normalizedData;

        if (state.dataNotSaved.findIndex((t: any) => t == trackTypeId) === -1) {
          state.dataNotSaved.push(trackTypeId);
        }

        // Extend depth bounds to cover any edited fromDepth/toDepth values
        (data || []).forEach((row: any) => {
          [row.depth, row.fromDepth, row.toDepth].forEach((d: any) => {
            if (typeof d !== 'number') return;
            if (state.minDepth === null || d < state.minDepth) state.minDepth = d;
            if (state.maxDepth === null || d > state.maxDepth) state.maxDepth = d;
          });
        });
      },
      changeStratigraphyData: (state, action: PayloadAction<any>) => {
        const { trackTypeId, data, showView, boreholeId } = action.payload;
        
        let track = showView
          ? state.groupTrackData.find((t: any) => t.trackTypeId === trackTypeId && t.boreholeId === boreholeId)
          : state.trackData.find((t: any) => t.trackTypeId === trackTypeId);

        if (!track) {
          track = showView
            ? { trackTypeId, boreholeId, data: [] }
            : { trackTypeId, data: [] };
          if (showView) {
            state.groupTrackData.push(track);
          } else {
            state.trackData.push(track);
          }
        }

        track.data = Array.isArray(data) ? data : [];

        if (state.dataNotSaved.findIndex((t: any) => t == trackTypeId) === -1) {
          state.dataNotSaved.push(trackTypeId);
        }

        // Extend depth bounds so the chart and slider reflect any edited fromDepth/toDepth values
        // without needing a backend round-trip.
        (data || []).forEach((row: any) => {
          [row.depth, row.fromDepth, row.toDepth].forEach((d: any) => {
            if (typeof d !== 'number') return;
            if (state.minDepth === null || d < state.minDepth) state.minDepth = d;
            if (state.maxDepth === null || d > state.maxDepth) state.maxDepth = d;
          });
        });
      },
      addStratigraphyValue: (state, action: PayloadAction<any>) => {                
        const { id, stratigraphyTrackTypeId, fromDepth, toDepth, value, color, stratigraphyLexiconId, showView, boreholeId } = action.payload;
        // sortedTrackData reads text/stratigraphy data from groupTrackData regardless of mode,
        // so always search and write there.
        let track = state.groupTrackData.find((t: any) => t.trackTypeId === stratigraphyTrackTypeId && t.boreholeId === boreholeId);
        if (!track) {
          track = { trackTypeId: stratigraphyTrackTypeId, boreholeId, data: [] };
          state.groupTrackData.push(track);
        }
        if (!track.data) {
          track.data = [];
        }
        const trackData = track.data;
        // Use negative IDs for new items to distinguish from database IDs
        const newId = id ?? Math.min(0, ...trackData.map((item: any) => typeof item.id === 'number' ? item.id : 0)) - 1;
        const data = { id: newId, fromDepth, toDepth, value, stratigraphyLexiconId, backgroundColor: color };
        trackData.push(data);
        trackData.sort((a: any, b: any) => a.fromDepth - b.fromDepth);
        // Immediately extend the global depth bounds so the viewer can show the new row
        // without waiting for the next backend fetch.
        if (typeof fromDepth === 'number') {
          if (state.minDepth === null || fromDepth < state.minDepth) state.minDepth = fromDepth;
          if (state.maxDepth === null || fromDepth > state.maxDepth) state.maxDepth = fromDepth;
        }
        if (typeof toDepth === 'number') {
          if (state.minDepth === null || toDepth < state.minDepth) state.minDepth = toDepth;
          if (state.maxDepth === null || toDepth > state.maxDepth) state.maxDepth = toDepth;
        }
        if (state.dataNotSaved.findIndex((t: any) => t == stratigraphyTrackTypeId) === -1) {
          state.dataNotSaved.push(stratigraphyTrackTypeId);
        }
      }      ,
      setTrackEditMode: (state, action: PayloadAction<any>) => {
        const { trackId, editMode } = action.payload;     
        const track = state.template?.tracks.find((t: any) => t.id == trackId);
        track.trackEditMode = editMode;        
      }, 
      addNewTemplate: (state) => {       
        state.selectedBuiltInTemplateId = null;
        state.selectedUserTemplateId = null;
        state.selectedBoreholeTemplateId = null;              
        state.selectedProjectTemplateId = null;
        state.selectedClientTemplateId = null;
        state.selectedTemplateId = null;
        state.canSaveTemplate = false;
        state.template = {...emptyTemplate};
      },
      clearTrackData: (state) => {
        state.trackData = [];
        // Preserve any groupTrackData entries that have unsaved rows (negative IDs)
        // so that newly added stratigraphy / text values survive depth-range re-fetches.
        state.groupTrackData = state.groupTrackData.filter((entry: any) =>
          Array.isArray(entry?.data) && entry.data.some((row: any) => typeof row.id === 'number' && row.id < 0)
        );
        // Reset depth extents so stale unit-converted values from the previous
        // depth unit do not cause the templateMinDepth/templateMaxDepth extension
        // effect to push endDepth into the wrong range.
        state.minDepth = null;
        state.maxDepth = null;
      },
      clearUrlTemplateId: (state) => {
        state.urlTemplateId = null;
      },
      updateTrackDataCache: (state, action: PayloadAction<{
        trackTypeId: number,
        boreholeId?: number,
        scale: number,
        startDepth: number,
        endDepth: number,
        data: any[]
      }>) => {
        const { trackTypeId, boreholeId, scale, startDepth, endDepth, data } = action.payload;
        const cacheKey = getCacheKey(trackTypeId, boreholeId, scale);

        // If adding a coarser scale (higher scale number), remove all finer scale (lower scale number) cache entries for the same trackTypeId/boreholeId and overlapping depth range
        Object.keys(state.trackDataCache).forEach(key => {
          const entry = state.trackDataCache[key];
          if (
            entry.trackTypeId === trackTypeId &&
            entry.boreholeId === boreholeId &&
            entry.scale < scale && // finer scale
            // Check for overlap in depth range
            !(entry.endDepth < startDepth || entry.startDepth > endDepth)
          ) {
            delete state.trackDataCache[key];
          }
        });

        state.trackDataCache[cacheKey] = {
          trackTypeId,
          boreholeId,
          scale,
          startDepth,
          endDepth,
          data,
          timestamp: Date.now()
        };
      },
      clearTrackDataCache: (state) => {
        state.trackDataCache = {};
      }
      ,
      clearScaleMetadata: (state) => {
        state.scaleMetadata = [];
        state.scaleMetadataByBorehole = {};
        state.scalesLoaded = false;
        // Reset depth extents so stale unit-converted scale values don't
        // cause the depth-range extension effect to push endDepth into the wrong unit.
        state.minDepth = null;
        state.maxDepth = null;
      },
      mergeDirectTrackData: (state, action: PayloadAction<{
        groupTrackData: any[];
        scaleMetadataByBorehole: { [boreholeId: string]: any[] };
        groupTrackImages: any[];
      }>) => {
        const { groupTrackData, scaleMetadataByBorehole, groupTrackImages } = action.payload;

        // Merge groupTrackData – replace existing entry by boreholeId+trackTypeId, add if absent
        groupTrackData.forEach((incoming: any) => {
          const bId = Number(incoming?.boreholeId);
          const ttId = Number(incoming?.trackTypeId);
          if (isNaN(bId) || isNaN(ttId)) return;
          const idx = state.groupTrackData.findIndex(
            (t: any) => Number(t.boreholeId) === bId && Number(t.trackTypeId) === ttId
          );
          if (idx >= 0) {
            // Preserve any unsaved rows (negative IDs) that exist in the current entry
            // so they survive a re-fetch triggered by a depth-range change.
            const unsavedRows = (state.groupTrackData[idx].data || []).filter(
              (row: any) => typeof row.id === 'number' && row.id < 0
            );
            const incomingData = Array.isArray(incoming.data) ? incoming.data : [];
            const dedupedUnsavedRows = unsavedRows.filter((row: any) => {
              if (typeof row?.depth === 'number') {
                return !incomingData.some((d: any) => Number(d?.depth) === Number(row.depth));
              }
              return !incomingData.some(
                (d: any) => Number(d?.fromDepth) === Number(row?.fromDepth) && Number(d?.toDepth) === Number(row?.toDepth)
              );
            });
            const mergedData = dedupedUnsavedRows.length > 0
              ? [...incomingData, ...dedupedUnsavedRows].sort((a: any, b: any) => (a?.fromDepth ?? a?.depth ?? 0) - (b?.fromDepth ?? b?.depth ?? 0))
              : incomingData;
            state.groupTrackData[idx] = { ...state.groupTrackData[idx], ...incoming, data: mergedData };
          } else {
            state.groupTrackData.push(incoming);
          }
          // Mirror into flat trackData so single-borehole template mode reads it via sortedTrackData
          const data = incoming?.data;
          if (Array.isArray(data) && data.length > 0) {
            const existingIdx = state.trackData.findIndex((t: any) => Number(t.trackTypeId) === ttId);
            if (existingIdx >= 0) {
              state.trackData[existingIdx] = { ...incoming };
            } else {
              state.trackData.push({ ...incoming });
            }
          }
        });

        // Merge scaleMetadataByBorehole
        Object.entries(scaleMetadataByBorehole).forEach(([bhId, scales]) => {
          const existing = state.scaleMetadataByBorehole[bhId] || [];
          const byTrackTypeId: { [id: number]: any } = {};
          existing.forEach((m: any) => {
            if (typeof m?.trackTypeId === 'number') byTrackTypeId[m.trackTypeId] = m;
          });
          (scales as any[]).forEach((m: any) => {
            if (typeof m?.trackTypeId === 'number') byTrackTypeId[m.trackTypeId] = m;
          });
          state.scaleMetadataByBorehole[bhId] = Object.values(byTrackTypeId);
        });

        // Rebuild flat scaleMetadata array and update overall depth range
        const allScales: any[] = [];
        Object.values(state.scaleMetadataByBorehole).forEach((arr: any) => {
          if (Array.isArray(arr)) allScales.push(...arr);
        });
        state.scaleMetadata = allScales;

        // Update overall min/max depth from the merged scale metadata
        Object.values(scaleMetadataByBorehole).forEach((scales) => {
          (scales as any[]).forEach((m: any) => {
            if (typeof m?.minimumDepth === 'number') {
              if (state.minDepth === null || m.minimumDepth < state.minDepth) state.minDepth = m.minimumDepth;
            }
            if (typeof m?.maximumDepth === 'number') {
              if (state.maxDepth === null || m.maximumDepth > state.maxDepth) state.maxDepth = m.maximumDepth;
            }
          });
        });

        // Merge groupTrackImages
        const existingKeys = new Set(
          state.groupTrackImages.map((ti: any) => `${ti?.boreholeId || ''}_${ti?.imageType || ''}_${ti?.id || ''}`)
        );
        (groupTrackImages as any[]).forEach((ti: any) => {
          const key = `${ti?.boreholeId || ''}_${ti?.imageType || ''}_${ti?.id || ''}`;
          if (!existingKeys.has(key)) {
            state.groupTrackImages.push(ti);
            existingKeys.add(key);
          }
        });

        state.scalesLoaded = true;
      }
    },
    extraReducers:  (builder) => {
        builder.addCase(getTemplates.pending, (state, { payload }) => {
            state.templatesLoading = true;
            state.templatesLoaded = false;
            state.templateSaveError = null;
            state.error = null;
          });
        builder.addCase(getTemplates.fulfilled, (state, action) => {          
            const { payload } = action;
            state.templatesLoading = false;
            if (!payload.success) {
              state.error = payload.errors;
            } else {              
              state.allTemplates = payload.allTemplates;
              state.builtInTemplates = payload.builtInTemplates;
              state.userTemplates = payload.userTemplates;
              state.boreholeTemplates = payload.boreholeTemplates;
              state.projectTemplates = payload.projectTemplates;
              state.clientTemplates = payload.clientTemplates;
              state.templateLevels = payload.templateLevels;              
              state.templatesLoaded = true;
              sortTemplates(state);
            }
          });
        builder.addCase(getTemplates.rejected, (state, action) => {         
            state.templatesLoading = false;
            state.error = action.error;
          });

        builder.addCase(getClientTemplates.pending, (state, { payload }) => {
            state.templatesLoading = true;
            state.templatesLoaded = false;
            state.error = null;
          });
        builder.addCase(getClientTemplates.fulfilled, (state, action) => {          
            const { payload } = action;
            state.templatesLoading = false;
            if (!payload.success) {
              state.error = payload.errors;
            } else {              
              state.builtInTemplates = payload.builtInTemplates;
              state.userTemplates = payload.userTemplates;
              state.projectTemplates = payload.projectTemplates;
              state.clientTemplates = payload.clientTemplates;
              state.templatesLoaded = true;
              sortTemplates(state);
            }
          });
        builder.addCase(getClientTemplates.rejected, (state, action) => {         
            state.templatesLoading = false;
            state.error = action.error;
          });

        builder.addCase(getTemplate.pending, (state, { payload }) => {
            state.templateLoading = true;
            state.templateLoaded = false;
          });

        builder.addCase(getTemplate.rejected, (state, { payload }) => {
            state.templateLoading = false;            
          });

        builder.addCase(getTemplate.fulfilled, (state, { payload }:any) => {
          state.templateLoading = false;
          state.templateLoaded = true;
          if (!payload.success) {
            state.error = payload.errors;
          } else {
            
            const template: any = payload;
            template.tracks.forEach((track: any) => {
              track.collapsed = true;
            });
            
            state.template = template;
          }
          });

        builder.addCase(updateTemplate.pending, (state, { payload, meta }) => {             
          state.error = null;
          state.templateSaveError = null;
        });

        builder.addCase(updateTemplate.fulfilled, (state, { payload, meta }) => {             
            if (payload?.success || payload.status == 200) {
              // Update the name in allTemplates so dropdowns reflect the new name immediately
              const savedTemplate = meta.arg;
              if (savedTemplate?.id != null && savedTemplate?.name != null) {
                const idx = state.allTemplates?.findIndex((t: any) => t.id === savedTemplate.id);
                if (idx != null && idx >= 0) {
                  state.allTemplates[idx].name = savedTemplate.name;
                }
                // Also update the level-specific arrays
                for (const arr of [state.builtInTemplates, state.userTemplates, state.boreholeTemplates, state.projectTemplates, state.clientTemplates]) {
                  if (!arr) continue;
                  const i = arr.findIndex((t: any) => t.id === savedTemplate.id);
                  if (i >= 0) arr[i].name = savedTemplate.name;
                }
                // Update the loaded template name too
                if (state.template && state.template.id === savedTemplate.id) {
                  state.template.name = savedTemplate.name;
                }
              }
              if (state.nextTemplateId) {              
                  // state.selectedBuiltInTemplateId = null;
                  // state.selectedUserTemplateId = null;
                  // state.selectedBoreholeTemplateId = null;
                  // state.selectedProjectTemplateId = null;
                  // state.selectedClientTemplateId = null;
                switch (state.nextTemplateLevel) {
                  case 0: 
                    state.selectedBuiltInTemplateId = state.nextTemplateId;
                    break;
                  case 1: 
                    state.selectedUserTemplateId = state.nextTemplateId;
                    break;
                  case 2: 
                    state.selectedBoreholeTemplateId = state.nextTemplateId;
                    break;
                  case 3: 
                    state.selectedProjectTemplateId = state.nextTemplateId;
                    break;
                  case 4: 
                    state.selectedClientTemplateId = state.nextTemplateId;
                    break;                  
                }
                
                state.nextTemplateId = null;
              } else {
                state.selectedBuiltInTemplateId = null;
                state.selectedUserTemplateId = null;
                state.selectedBoreholeTemplateId = null;              
                state.selectedProjectTemplateId = null;
                state.selectedClientTemplateId = null;
              }

              state.notSaved = false;
              toast.success("Template saved");
            } else {
              state.templateSaveError = { message: "Template failed to save" };
              toast.error("Template failed to save");
            }
          });

          builder.addCase(updateTemplate.rejected, (state, action) => {            
            if (action.payload) {
              state.error = action.payload;
              state.templateSaveError = { errors: action.payload };
              toast.error("Template failed to save");
            } else {
              state.error = action.error;
              state.templateSaveError = { errors: action.error };
              toast.error("Template failed to save");
            }
          });

          builder.addCase(deleteTemplate.pending, (state, { payload, meta }) => {   
            state.error = null;
          });

          builder.addCase(deleteTemplate.fulfilled, (state, { payload, meta }) => {   
            if (payload?.success || payload.status == 200) {
              const templateId = payload.templateId;              
              const deletedIndex = state.allTemplates.findIndex((t: any) => t.id === templateId);
              state.allTemplates.splice(deletedIndex, 1);

              // switch (meta.arg.templateLevel) {
              //   case 0: 
              //     state.selectedBuiltInTemplateId = state.nextTemplateId;
              //     break;
              //   case 1: 
              //     state.selectedUserTemplateId = state.nextTemplateId;
              //     break;
              //   case 2: 
              //     state.selectedBoreholeTemplateId = state.nextTemplateId;
              //     break;
              //   case 3: 
              //     state.selectedProjectTemplateId = state.nextTemplateId;
              //     break;
              //   case 4: 
              //     state.selectedClientTemplateId = state.nextTemplateId;
              //     break;
              // }

              state.selectedBuiltInTemplateId = null;
              state.selectedUserTemplateId = null;
              state.selectedBoreholeTemplateId = null;              
              state.selectedProjectTemplateId = null;
              state.selectedClientTemplateId = null;

              state.selectedTemplateId = null;
              state.template = {...emptyTemplate}; //{};
              // if (state.userTemplates.length > 0) {
              //   const selectedTemplate = state.userTemplates[0];
              //   state.selectedBuiltInTemplateId = selectedTemplate.id;
              //   state.canSaveTemplate = !selectedTemplate?.cannotOverwrite;
              // }
              toast.success("Template deleted");
            } else {
              toast.error("Template failed to delete");
            }
          });

          builder.addCase(deleteTemplate.rejected, (state, action) => {
            if (action.payload) {
              state.error = action.payload;
              toast.error("Template failed to delete");
            } else {
              state.error = action.error;
              toast.error("Template failed to delete");
            }
          })

          builder.addCase(addTemplate.pending, (state) => {
            state.templateSaveError = null;
          });

          builder.addCase(addTemplate.fulfilled, (state, { payload, meta }: any) => {
            //add to template list  
            if (payload.status && payload.status !== 200) {
              state.templateSaveError = { message: "Template failed to save" };
              toast.error("Template failed to save");
              return;
            }   
            
            state.allTemplates.push({ id: payload.templateId, name: payload.name, cannotOverwrite: false, containsData: true, templateLevel: meta.arg.templateLevel });
            switch (meta.arg.templateLevel) {
              case 0: 
              state.builtInTemplates.push({ id: payload.templateId, name: payload.name, cannotOverwrite: false, containsData: true });
              break;
            case 1: 
              state.userTemplates.push({ id: payload.templateId, name: payload.name, cannotOverwrite: false, containsData: true });
              break;
            case 2: 
              state.boreholeTemplates.push({ id: payload.templateId, name: payload.name, cannotOverwrite: false, containsData: true });
              break;
            case 3: 
              state.projectTemplates.push({ id: payload.templateId, name: payload.name, cannotOverwrite: false, containsData: true });
              break;
            case 4: 
              state.clientTemplates.push({ id: payload.templateId, name: payload.name, cannotOverwrite: false, containsData: true });
              break;
            }
            
            if (!state.template) {
              state.template = {};
            }
            
            state.template.name = payload.name;            
            state.canSaveTemplate = true;            
            sortTemplates(state);
            if (state.nextTemplateId) 
            {   
                state.urlTemplateId = state.nextTemplateId;
                state.nextTemplateId = null;
            } else {
              state.urlTemplateId = payload.templateId;              
            }            
            state.notSaved = false;
            toast.success("Template saved");
          });

          builder.addCase(addTemplate.rejected, (state, { payload }) => {                      
            state.templateSaveError = state.templateSaveError = { errors: payload };;
            toast.error("Template failed to save");
          });

          builder.addCase(getMultipleBoreholeTrackDataForTrackTypes.pending, (state, { payload }) => {
            state.error = null;
            state.trackDataLoading  = true;
            //state.minDepth = null;
            //state.maxDepth = null;
          });

          builder.addCase(getMultipleBoreholeTrackDataForTrackTypes.rejected, (state, { payload }) => {
            state.trackDataLoading  = false;
            state.error = payload;
          });

          builder.addCase(getMultipleBoreholeTrackDataForTrackTypes.fulfilled, (state, action) => {                        
            state.trackDataLoading = false;
            state.error = null;
            if (action.payload) {              
              action.payload.trackData?.forEach((incoming: any) => {
                const bId = Number(incoming?.boreholeId);
                const ttId = Number(incoming?.trackTypeId);
                const idx = state.groupTrackData.findIndex(
                  (t: any) => Number(t.boreholeId) === bId && Number(t.trackTypeId) === ttId
                );
                if (idx >= 0) {
                  const unsaved = (state.groupTrackData[idx].data || []).filter((r: any) => typeof r.id === 'number' && r.id < 0);
                  const incomingData = Array.isArray(incoming.data) ? incoming.data : [];
                  const dedupedUnsaved = unsaved.filter((row: any) => {
                    if (typeof row?.depth === 'number') {
                      return !incomingData.some((d: any) => Number(d?.depth) === Number(row.depth));
                    }
                    return !incomingData.some(
                      (d: any) => Number(d?.fromDepth) === Number(row?.fromDepth) && Number(d?.toDepth) === Number(row?.toDepth)
                    );
                  });
                  const merged = dedupedUnsaved.length > 0
                    ? [...incomingData, ...dedupedUnsaved].sort((a: any, b: any) => (a?.fromDepth ?? a?.depth ?? 0) - (b?.fromDepth ?? b?.depth ?? 0))
                    : incomingData;
                  state.groupTrackData[idx] = { ...state.groupTrackData[idx], ...incoming, data: merged };
                } else {
                  state.groupTrackData.push(incoming);
                }
              });
              action.payload.trackImages?.forEach((trackImage: any) => state.groupTrackImages.push(trackImage));                    
              state.minDepth = action.payload.minimumDepth;
              state.maxDepth = action.payload.maximumDepth;
              // Ensure unsaved (negative-ID) rows are never clipped by API-returned depth extents.
              // Check ALL depth fields per row (a stratigraphy interval has fromDepth + toDepth;
              // only checking one could miss a toDepth that extends beyond the API max).
              state.groupTrackData.forEach((entry: any) => {
                (entry?.data || []).forEach((row: any) => {
                  if (typeof row.id !== 'number' || row.id >= 0) return;
                  [row.depth, row.fromDepth, row.toDepth].forEach((d) => {
                    if (typeof d !== 'number') return;
                    if (state.minDepth === null || d < state.minDepth) state.minDepth = d;
                    if (state.maxDepth === null || d > state.maxDepth) state.maxDepth = d;
                  });
                });
              });                    
            }            
          });    
          
          builder.addCase(getMultipleBoreholeTrackDataForTemplate.pending, (state, { payload }) => {
            state.error = null;
            state.trackDataLoading  = true;
            //state.minDepth = null;
            //state.maxDepth = null;
          });

          builder.addCase(getMultipleBoreholeTrackDataForTemplate.rejected, (state, { payload }) => {
            state.trackDataLoading  = false;
            state.error = payload;
          });

          builder.addCase(getMultipleBoreholeTrackDataForTemplate.fulfilled, (state, action) => {            
            state.trackDataLoading = false;
            state.error = null;            
            if (action.payload) {
              action.payload.trackData?.forEach((incoming: any) => {
                const bId = Number(incoming?.boreholeId);
                const ttId = Number(incoming?.trackTypeId);
                const idx = state.groupTrackData.findIndex(
                  (t: any) => Number(t.boreholeId) === bId && Number(t.trackTypeId) === ttId
                );
                if (idx >= 0) {
                  const unsaved = (state.groupTrackData[idx].data || []).filter((r: any) => typeof r.id === 'number' && r.id < 0);
                  const merged = unsaved.length > 0
                    ? [...(incoming.data || []), ...unsaved].sort((a: any, b: any) => a.fromDepth - b.fromDepth)
                    : incoming.data;
                  state.groupTrackData[idx] = { ...state.groupTrackData[idx], ...incoming, data: merged };
                } else {
                  state.groupTrackData.push(incoming);
                }
              });
              action.payload.trackImages?.forEach((trackImage: any) => state.groupTrackImages.push(trackImage));              
              state.minDepth = action.payload.minimumDepth;
              state.maxDepth = action.payload.maximumDepth;
              // Ensure unsaved (negative-ID) rows are never clipped by API-returned depth extents.
              // Check ALL depth fields per row (a stratigraphy interval has fromDepth + toDepth;
              // only checking one could miss a toDepth that extends beyond the API max).
              state.groupTrackData.forEach((entry: any) => {
                (entry?.data || []).forEach((row: any) => {
                  if (typeof row.id !== 'number' || row.id >= 0) return;
                  [row.depth, row.fromDepth, row.toDepth].forEach((d) => {
                    if (typeof d !== 'number') return;
                    if (state.minDepth === null || d < state.minDepth) state.minDepth = d;
                    if (state.maxDepth === null || d > state.maxDepth) state.maxDepth = d;
                  });
                });
              });
            }            
          });    

          builder.addCase(getMultipleBoreholeTrackDataForView.pending, (state, { payload }) => {
            state.error = null;
            state.trackDataLoading  = true;
            //state.minDepth = null;
            //state.maxDepth = null;
          });

          builder.addCase(getMultipleBoreholeTrackDataForView.rejected, (state, { payload }) => {
            state.trackDataLoading  = false;
            state.error = payload;
          });

          builder.addCase(getMultipleBoreholeTrackDataForView.fulfilled, (state, action) => {
            state.trackDataLoading = false;
            state.error = null;            
            if (action.payload) {
              action.payload.trackData?.forEach((incoming: any) => {
                const bId = Number(incoming?.boreholeId);
                const ttId = Number(incoming?.trackTypeId);
                const idx = state.groupTrackData.findIndex(
                  (t: any) => Number(t.boreholeId) === bId && Number(t.trackTypeId) === ttId
                );
                if (idx >= 0) {
                  const unsaved = (state.groupTrackData[idx].data || []).filter((r: any) => typeof r.id === 'number' && r.id < 0);
                  const merged = unsaved.length > 0
                    ? [...(incoming.data || []), ...unsaved].sort((a: any, b: any) => a.fromDepth - b.fromDepth)
                    : incoming.data;
                  state.groupTrackData[idx] = { ...state.groupTrackData[idx], ...incoming, data: merged };
                } else {
                  state.groupTrackData.push(incoming);
                }
              });
              action.payload.trackImages?.forEach((trackImage: any) => state.groupTrackImages.push(trackImage));
              state.minDepth = action.payload.minimumDepth;
              state.maxDepth = action.payload.maximumDepth;
              // Ensure unsaved (negative-ID) rows are never clipped by API-returned depth extents.
              // Check ALL depth fields per row (a stratigraphy interval has fromDepth + toDepth;
              // only checking one could miss a toDepth that extends beyond the API max).
              state.groupTrackData.forEach((entry: any) => {
                (entry?.data || []).forEach((row: any) => {
                  if (typeof row.id !== 'number' || row.id >= 0) return;
                  [row.depth, row.fromDepth, row.toDepth].forEach((d) => {
                    if (typeof d !== 'number') return;
                    if (state.minDepth === null || d < state.minDepth) state.minDepth = d;
                    if (state.maxDepth === null || d > state.maxDepth) state.maxDepth = d;
                  });
                });
              });
            }            
          });    

          builder.addCase(getTrackDataForImageTrackTypes.pending, (state, { payload }) => {
            state.trackDataLoading  = true;
          });

          builder.addCase(getTrackDataForImageTrackTypes.rejected, (state, { payload }) => {
            state.trackDataLoading  = false;
            state.error = payload;
          });

          builder.addCase(getTrackDataForImageTrackTypes.fulfilled, (state, action) => {
            //state.trackDataLoading  = false;
            //state.error = null;
            
            //state.imageMetaData = {...state.imageMetaData, ...action.payload.deepZoomMetaData };
            //action.payload.deepZoomMetaData.forEach((trackData: any) => state.trackData.push(trackData));            
            
            return {
              ...state,
              trackDataLoading: false,
              error: null,
              deepZoomMetaData: {...state.deepZoomMetaData, ...action.payload.deepZoomMetaData, boreholeName: action.payload.boreholeName }};
          });    

          builder.addCase(getMultipleBoreholeTextData.pending, (state, { payload }) => {            
          });

          builder.addCase(getMultipleBoreholeTextData.rejected, (state, { payload }) => {            
            state.error = payload;
          });

          builder.addCase(getMultipleBoreholeTextData.fulfilled, (state, action) => {            
            return {
              ...state,              
              error: null,
              boreholeTextTrackData: action.payload.boreholeTextTrackData };
          });
          
          builder.addCase(getMultipleBoreholeStratigraphyData.pending, (state, { payload }) => {            
          });

          builder.addCase(getMultipleBoreholeStratigraphyData.rejected, (state, { payload }) => {            
            state.error = payload;
          });

          builder.addCase(getMultipleBoreholeStratigraphyData.fulfilled, (state, action) => {            
            return {
              ...state,              
              error: null,
              boreholeStratigraphyTrackData: action.payload.boreholeStratigraphyTrackData };
          });
          
          builder.addCase(changeTemplateLevel.fulfilled, (state, action) => {
            const { meta } = action;             
            const templateId = meta.arg.id;
            let originalTemplate = state.allTemplates.find((t: any) => t.id === templateId);
            originalTemplate.templateLevel = meta.arg.templateLevel            
            state.selectedBuiltInTemplateId = null;
            state.selectedUserTemplateId = null;
            state.selectedBoreholeTemplateId = null;
            state.selectedProjectTemplateId = null;              
            state.selectedClientTemplateId = null;

            switch (meta.arg.templateLevel) {
              case 0: 
                state.selectedBuiltInTemplateId = templateId;
                break;
              case 1: 
                state.selectedUserTemplateId = templateId;
                break;
              case 2: 
                state.selectedBoreholeTemplateId = templateId;
                break;
              case 3: 
                state.selectedProjectTemplateId = templateId;
                break;
              case 4: 
                state.selectedClientTemplateId = templateId;
                break;
              }            
          });    

          builder.addCase(saveLithologyData.pending, (state, { payload }) => {
            state.trackDataLoading = true;
          });

          builder.addCase(saveLithologyData.rejected, (state, { payload }) => {
            state.trackDataLoading = false;
            state.error = payload;
          });

          builder.addCase(saveLithologyData.fulfilled, (state, action) => {
            state.trackDataLoading = false;            
            if (action.payload.success) {
              const trackTypeId = action.meta.arg.data.trackTypeId;
              const boreholeId = action.meta.arg.boreholeId;
              const index = state.dataNotSaved.findIndex((t: any) => t == trackTypeId)
              if (index >= 0) {
                state.dataNotSaved.splice(index, 1);
              }

              // Update IDs from backend response if provided
              if (action.payload.data) {
                // Update legacy trackData for backward compatibility
                const track = state.trackData.find((t: any) => t.trackTypeId === trackTypeId);
                if (track && track.data) {
                  track.data = action.payload.data;
                }
                
                // Update canonical groupTrackData (used by renderer)
                if (boreholeId) {
                  let groupTrack = state.groupTrackData.find((t: any) => t.trackTypeId === trackTypeId && t.boreholeId === boreholeId);
                  if (!groupTrack) {
                    groupTrack = { trackTypeId, boreholeId, data: [] };
                    state.groupTrackData.push(groupTrack);
                  }
                  groupTrack.data = action.payload.data;
                }
              }

              toast.success("Data saved");            
            } else {
              toast.error("Error saving data");            
            }
          });   
          
          builder.addCase(saveTextData.pending, (state, { payload }) => {
            state.trackDataLoading = true;
          });

          builder.addCase(saveTextData.rejected, (state, { payload }) => {
            state.trackDataLoading = false;
            state.error = payload;
          });

          builder.addCase(saveTextData.fulfilled, (state, action) => {
            state.trackDataLoading = false;                        
            if (action.payload.success) {
              const trackTypeId = action.meta.arg.trackTypeId;
              const boreholeId = action.meta.arg.boreholeId;
              const index = state.dataNotSaved.findIndex((t: any) => t == trackTypeId)
              if (index >= 0) {
                state.dataNotSaved.splice(index, 1);
              }

              // Update IDs from backend response if provided
              if (action.payload.data) {
                const normalizedData = (action.payload.data || []).map((row: any) => {
                  const bg = row?.backgroundColor ?? row?.color;
                  return bg ? { ...row, backgroundColor: bg, color: bg } : row;
                });

                // Keep legacy template-mode source in sync.
                const track = state.trackData.find((t: any) => t.trackTypeId === trackTypeId);
                if (track && track.data) {
                  track.data = normalizedData;
                }

                // Update render source used by text tracks.
                const groupTrack = state.groupTrackData.find(
                  (t: any) => t.trackTypeId === trackTypeId && t.boreholeId === boreholeId
                );
                if (groupTrack) {
                  groupTrack.data = normalizedData;
                } else {
                  state.groupTrackData.push({ trackTypeId, boreholeId, data: normalizedData });
                }
              }

              toast.success("Data saved");            
            } else {
              toast.error("Error saving data");            
            }
          });   

          builder.addCase(saveStratigraphyData.pending, (state, { payload }) => {
            state.trackDataLoading = true;
          });

          builder.addCase(saveStratigraphyData.rejected, (state, { payload }) => {
            state.trackDataLoading = false;
            state.error = payload;
          });

          builder.addCase(saveStratigraphyData.fulfilled, (state, action) => {
            state.trackDataLoading = false;                        
            if (action.payload.success) {
              const { trackTypeId, boreholeId, data: savedData } = action.meta.arg;
              const index = state.dataNotSaved.findIndex((t: any) => t == trackTypeId);
              if (index >= 0) {
                state.dataNotSaved.splice(index, 1);
              }

              // The backend returns the saved rows (with real server IDs).
              // Update both groupTrackData (multi-borehole / view mode) and
              // trackData (single-borehole template mode) so the chart reflects
              // the persisted state without a full re-fetch.
              const responseData: any[] = action.payload.data ?? savedData ?? [];

              // --- groupTrackData (primary data source for the viewer) ---
              const groupEntry = state.groupTrackData.find(
                (t: any) => Number(t.trackTypeId) === Number(trackTypeId) &&
                            Number(t.boreholeId) === Number(boreholeId)
              );
              if (groupEntry) {
                groupEntry.data = responseData;
              }

              // --- flat trackData (single-borehole template mode fallback) ---
              const flatTrack = state.trackData.find((t: any) => t.trackTypeId === trackTypeId);
              if (flatTrack) {
                flatTrack.data = responseData;
              }

              // Extend depth bounds to cover the saved rows
              responseData.forEach((row: any) => {
                [row.depth, row.fromDepth, row.toDepth].forEach((d: any) => {
                  if (typeof d !== 'number') return;
                  if (state.minDepth === null || d < state.minDepth) state.minDepth = d;
                  if (state.maxDepth === null || d > state.maxDepth) state.maxDepth = d;
                });
              });

              toast.success("Stratigraphy data saved");            
            } else {
              toast.error("Error saving stratigraphy data");            
            }
          });   

          // Resolution metadata
          builder.addCase(getScaleMetadata.pending, (state) => {            
            state.scalesLoaded = false;
            state.scalesLoading = true;
            state.error = null;
          });

          builder.addCase(getScaleMetadata.rejected, (state, { payload }) => {            
            state.scalesLoading = false;
            state.error = payload;
          });

          builder.addCase(getScaleMetadata.fulfilled, (state, action) => {
            state.scalesLoading = false;
            state.scalesLoaded = true;
            const { payload } = action;
            
            if (payload) {
              // Handle flat array format with boreholeId in each item
              if (payload.trackDataScales && Array.isArray(payload.trackDataScales)) {
                // Group by boreholeId
                const byBorehole: { [boreholeId: string]: ScaleMetadata[] } = {};
                
                payload.trackDataScales.forEach((item: any) => {
                  const bhId = String(item.boreholeId);
                  if (!byBorehole[bhId]) {
                    byBorehole[bhId] = [];
                  }
                  byBorehole[bhId].push(item);
                });
                
                // Store per-borehole scale metadata
                Object.keys(byBorehole).forEach((boreholeId: string) => {
                  const items = byBorehole[boreholeId];
                  const existing = state.scaleMetadataByBorehole[boreholeId] || [];
                  const byId: { [id: number]: ScaleMetadata } = {} as any;
                  
                  // Merge existing with new (new items win)
                  existing.forEach((m: any) => { byId[m.trackTypeId] = m; });
                  items.forEach((m: any) => { byId[m.trackTypeId] = m; });
                  
                  state.scaleMetadataByBorehole[boreholeId] = Object.values(byId);
                });
                
                // Also store in legacy single-borehole metadata for backward compatibility
                const existingLegacy = state.scaleMetadata || [];
                const byIdLegacy: { [id: number]: ScaleMetadata } = {} as any;
                existingLegacy.forEach((m: any) => { byIdLegacy[m.trackTypeId] = m; });
                payload.trackDataScales.forEach((m: any) => { 
                  byIdLegacy[m.trackTypeId] = m; 
                });
                state.scaleMetadata = Object.values(byIdLegacy);

                // Update overall depth range from the scales response
                payload.trackDataScales.forEach((item: any) => {
                  if (typeof item.minimumDepth === 'number') {
                    if (state.minDepth === null || item.minimumDepth < state.minDepth) state.minDepth = item.minimumDepth;
                  }
                  if (typeof item.maximumDepth === 'number') {
                    if (state.maxDepth === null || item.maximumDepth > state.maxDepth) state.maxDepth = item.maximumDepth;
                  }
                });
              }
              
              // Handle image metadata (flat array with boreholeId in each item)
              if (payload.trackImages && Array.isArray(payload.trackImages)) {
                const existingKeys = new Set(
                  state.groupTrackImages.map((ti: any) => `${ti.boreholeId || ''}_${ti.imageType || ''}_${ti.id || ''}`)
                );
                
                payload.trackImages.forEach((ti: any) => {
                  // Skip items marked as noImage unless needed for placeholder tracking
                  // if (ti.noImage) return;
                  
                  const key = `${ti.boreholeId || ''}_${ti.imageType || ''}_${ti.id || ''}`;
                  if (!existingKeys.has(key)) {
                    state.groupTrackImages.push(ti);
                    state.trackImages.push(ti);
                    existingKeys.add(key);
                  }
                });
              }
            }
          });
         
          // Single POST variant
          builder.addCase(postTrackDataForTrackTypesWithResolutions.pending, (state) => {
            state.loadingHighResolution = true;
            state.error = null;
          });

          builder.addCase(postTrackDataForTrackTypesWithResolutions.rejected, (state, { payload }) => {
            state.loadingHighResolution = false;
            state.error = payload;
          });

          builder.addCase(postTrackDataForTrackTypesWithResolutions.fulfilled, (state, action) => {
            state.loadingHighResolution = false;
            const { payload, meta } = action as any;
            if (payload && payload.trackData) {
              const boreholeIdNum = typeof meta.arg.boreholeId === 'string' ? parseInt(meta.arg.boreholeId) : meta.arg.boreholeId;
              const startDepth = meta.arg.startDepth;
              const endDepth = meta.arg.endDepth;
              const scaleMap: { [trackTypeId: number]: number } = {};
              meta.arg.trackTypes.forEach((e: any) => scaleMap[e.trackTypeId] = e.scale);

              payload.trackData.forEach((trackDataItem: any) => {
                const scale = scaleMap[trackDataItem.trackTypeId];
                if (typeof scale !== 'number') return;

                const cacheKey = getCacheKey(trackDataItem.trackTypeId, boreholeIdNum, scale);
                state.trackDataCache[cacheKey] = {
                  trackTypeId: trackDataItem.trackTypeId,
                  boreholeId: boreholeIdNum,
                  scale,
                  startDepth,
                  endDepth,
                  data: trackDataItem.data,
                  timestamp: Date.now()
                };

                const existingTrackIndex = state.trackData.findIndex(
                  (t: any) => t.trackTypeId === trackDataItem.trackTypeId
                );
                if (existingTrackIndex >= 0) {
                  state.trackData[existingTrackIndex] = trackDataItem;
                } else {
                  state.trackData.push(trackDataItem);
                }
              });
            }
          });

          // Multi-borehole POST variant
          builder.addCase(postMultiBoreholeTrackDataWithScales.pending, (state) => {
            state.trackDataLoading = true;
            state.error = null;
          });

          builder.addCase(postMultiBoreholeTrackDataWithScales.rejected, (state, { payload }) => {
            state.trackDataLoading = false;
            state.error = payload;
          });

          builder.addCase(postMultiBoreholeTrackDataWithScales.fulfilled, (state, action) => {
            state.trackDataLoading = false;
            const { payload, meta } = action as any;            
            
            // Expected response: { boreholeTrackData: [{ boreholeId, trackData: [...] }] }
            if (payload && payload.trackData) {              
              if (payload.minimumDepth != null) {
                state.minDepth = payload.minimumDepth;
              }
              if (payload.maximumDepth != null) {
                state.maxDepth = payload.maximumDepth;
              }
              // Ensure unsaved (negative-ID) rows are never clipped by API-returned depth extents.
              // Check ALL depth fields per row (a stratigraphy interval has fromDepth + toDepth;
              // only checking one could miss a toDepth that extends beyond the API max).
              state.groupTrackData.forEach((entry: any) => {
                (entry?.data || []).forEach((row: any) => {
                  if (typeof row.id !== 'number' || row.id >= 0) return;
                  [row.depth, row.fromDepth, row.toDepth].forEach((d) => {
                    if (typeof d !== 'number') return;
                    if (state.minDepth === null || d < state.minDepth) state.minDepth = d;
                    if (state.maxDepth === null || d > state.maxDepth) state.maxDepth = d;
                  });
                });
              });
              const startDepth = meta.arg.startDepth;
              const endDepth = meta.arg.endDepth;
              const singleBoreholeResponse = Array.isArray(meta.arg.boreholeEntries) && meta.arg.boreholeEntries.length === 1;
              const singleBoreholeId = singleBoreholeResponse ? meta.arg.boreholeEntries[0]?.boreholeId : undefined;

              // Build a map of boreholeId -> trackTypeId -> scale
              const scaleMap: { [boreholeId: string]: { [trackTypeId: number]: number } } = {};
              meta.arg.boreholeEntries.forEach((bhEntry: any) => {
                const bhId = String(bhEntry.boreholeId);
                scaleMap[bhId] = {};
                bhEntry.trackTypeEntries.forEach((ttEntry: any) => {
                  scaleMap[bhId][ttEntry.trackTypeId] = ttEntry.scale;
                });
              });

              payload.trackData.forEach((boreholeData: any) => {
                const boreholeIdNum = typeof boreholeData.boreholeId === 'string' 
                  ? parseInt(boreholeData.boreholeId) 
                  : boreholeData.boreholeId;
                const bhIdStr = String(boreholeData.boreholeId);

                const scaleFromRequest = scaleMap[bhIdStr]?.[boreholeData.trackTypeId];
                let scale = typeof scaleFromRequest === 'number'
                  ? scaleFromRequest
                  : (typeof boreholeData.scale === 'number' ? boreholeData.scale : undefined);
                if (typeof scale !== 'number') {
                  const existingScale = state.groupTrackData.find(
                    (t: any) => t.trackTypeId === boreholeData.trackTypeId && t.boreholeId === boreholeIdNum
                  )?.scale;
                  if (typeof existingScale === 'number') {
                    scale = existingScale;
                  }
                }
                if (typeof scale !== 'number') return;

                const rawDataArray = Array.isArray(boreholeData.data) ? boreholeData.data : [];
                const nestedData = rawDataArray.length > 0 && rawDataArray[0] && Array.isArray(rawDataArray[0].data)
                  ? rawDataArray[0].data
                  : rawDataArray;

                // Store in cache (for single-borehole enrichedTrackData logic)
                if (nestedData && nestedData.length > 0) {
                  const cacheKey = getCacheKey(boreholeData.trackTypeId, boreholeIdNum, scale);
                  state.trackDataCache[cacheKey] = {
                    trackTypeId: boreholeData.trackTypeId,
                    boreholeId: boreholeIdNum,
                    scale,
                    startDepth,
                    endDepth,
                    data: nestedData,
                    timestamp: Date.now()
                  };
                }

                // Update groupTrackData for multi-borehole views
                // Always update with the latest fetched data for this scale
                const existingGroupTrackIndex = state.groupTrackData.findIndex(
                  (t: any) => t.trackTypeId === boreholeData.trackTypeId && 
                              t.boreholeId === boreholeIdNum &&
                              t.scale === scale
                );
                                
                if (existingGroupTrackIndex >= 0) {
                  // Replace existing entry for this specific scale, preserving unsaved (negative-ID) rows
                  const unsavedRows = (state.groupTrackData[existingGroupTrackIndex].data || []).filter(
                    (row: any) => typeof row.id === 'number' && row.id < 0
                  );
                  const newData = nestedData.length > 0 ? nestedData : boreholeData.data;
                  state.groupTrackData[existingGroupTrackIndex].data = unsavedRows.length > 0
                    ? [...newData, ...unsavedRows].sort((a: any, b: any) => (a.depth ?? a.fromDepth ?? 0) - (b.depth ?? b.fromDepth ?? 0))
                    : newData;
                  state.groupTrackData[existingGroupTrackIndex].scale = scale;
                  state.groupTrackData[existingGroupTrackIndex].noData = boreholeData.noData;
                } else {
                  // Add new entry with scale information
                  state.groupTrackData.push({
                    data: nestedData.length > 0 ? nestedData : boreholeData.data,
                    boreholeId: boreholeIdNum,                    
                    trackTypeId: boreholeData.trackTypeId,
                    scale,
                    noData: boreholeData.noData                  
                  });
                }

                if (singleBoreholeResponse && String(boreholeData.boreholeId) === String(singleBoreholeId) && nestedData.length > 0) {
                  const existingTrackIndex = state.trackData.findIndex(
                    (t: any) => t.trackTypeId === boreholeData.trackTypeId
                  );
                  const normalized = {
                    ...boreholeData,
                    data: nestedData
                  };
                  if (existingTrackIndex >= 0) {
                    state.trackData[existingTrackIndex] = normalized;
                  } else {
                    state.trackData.push(normalized);
                  }
                }
              });
            }
          });
    }    
})

export const { addCurve,
               updateCurve,
               updateCurves,          
               setShowGridlines,
               setShowValueAxisAnnotation,
               setScaleType,
               setTrackWidth,
               setSelectedBuiltInTemplateId,
               setSelectedUserTemplateId,
               setSelectedBoreholeTemplateId,
               setSelectedProjectTemplateId,
               setSelectedClientTemplateId,   
               setSelectedTemplateId,            
               setNextTemplateId, 
               clearNotSaved,
               setTemplateName,
               removeTrack,
               updateTrackOrder,
               addGraphTracks,
               addImageTracks,
               addCumulativeTrack,
               addTadpoleTrack,
               addLithologyTrack,
               addTextTrack,
               setDepthMajorIntervals,
               setShowDepthGridlines,
               setDepthMinorIntervals, 
               setShowDepthMinorIntervals,
               setTrackImageFilter,
               clearTemplatesLoaded,
               clearTemplateLoading,
               scrollTrackIntoView,
               scrollAdjustmentIntoView,
               setTrackCollapsed,
               setDepthUnit,
               clearTemplate,
               setViewer,
               changeDataValue,
               changeDepth,
               addDataValue,
               deleteDataValue,
               setTrackEditMode,
               addNewTemplate,
               clearTrackData,
               clearUrlTemplateId,
               addTextValue,
               changeTextData,
               addStatisticsTracks,
               deleteStatisticsTrack,
               addHorizontalBarGraphTracks,
               addStratigraphyTrack,
               changeStratigraphyData,
               addStratigraphyValue,
               updateTrackDataCache,
               clearTrackDataCache,
               clearScaleMetadata,
               mergeDirectTrackData } = templateSlice.actions

export default templateSlice.reducer;